import React from "react";
import { Check } from "lucide-react";
import { ArtworkUpload } from "./ArtworkUpload";
import { formatAUDexact } from "../data/signsPricing";
import { formatCents } from "../data/pricing";
import { ROLL_MIN_CHARGE } from "../data/rollStickerPricing";

function Line({ label, value, testid, strong }) {
  return (
    <div className={`flex justify-between gap-4 py-2.5 border-b border-[#F0ECE6] last:border-0 ${strong ? "font-bold text-slate-900" : "text-slate-600"}`}>
      <span>{label}</span>
      <span className="text-right text-slate-900" data-testid={testid}>{value}</span>
    </div>
  );
}

function PriceBlock({ result, q }) {
  if (result.status !== "ok") return <div className="mt-3 text-sm font-semibold text-slate-600" data-testid="custom-price-message">{result.message}</div>;
  const gst = Math.round(result.price * 10) / 100;
  return (
    <>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-display text-5xl font-black price-tick" data-testid="custom-total-price">{formatAUDexact(result.price)}</div>
        <div className="text-sm text-slate-500 font-semibold">+ GST</div>
      </div>
      <div className="text-slate-600 mt-1" data-testid="custom-per-sticker">{formatCents(result.price / q)} per sticker</div>
      <div className="mt-3 text-xs text-slate-600 rounded-xl bg-[#F3EFEA] px-3 py-2" data-testid="custom-priced-at">
        Priced for <strong>{result.pricedSize}</strong> × {q.toLocaleString()} stickers.
      </div>
      {result.minimumApplied && <div className="mt-2 text-xs font-semibold text-[color:var(--brand)]" data-testid="custom-minimum-note">Minimum order charge of ${ROLL_MIN_CHARGE} applies.</div>}
      <div className="mt-4 text-sm" data-testid="custom-sheet-gst">
        <Line label="Subtotal (ex GST)" value={formatAUDexact(result.price)} testid="custom-sheet-subtotal" />
        <Line label="GST (10%)" value={formatAUDexact(gst)} testid="custom-sheet-gst-amount" />
        <Line label="Total (inc GST)" value={formatAUDexact(Math.round((result.price + gst) * 100) / 100)} testid="custom-sheet-total-inc" strong />
      </div>
    </>
  );
}

export function RollSummary({ result, q, rows, onAdd, file, setFile, copyright, setCopyright }) {
  return (
    <div className="bg-white rounded-3xl border border-[#ECE7E0] shadow-[0_10px_40px_-20px_rgba(17,19,21,0.25)] grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-[#ECE7E0]" data-testid="custom-price-card">
      <div className="p-6">
        <div className="font-display text-lg font-extrabold mb-1">Your selection</div>
        <div className="text-sm">{rows.map(([l, v, t]) => <Line key={t} label={l} value={v} testid={t} />)}</div>
      </div>

      <div className="p-6">
        <div className="flex justify-between items-baseline mb-3">
          <div className="font-display text-lg font-extrabold">Artwork</div>
          <span className="text-sm text-slate-500 truncate max-w-[60%]" data-testid="roll-summary-artwork">{file ? file.name : "Not uploaded"}</span>
        </div>
        <ArtworkUpload file={file} setFile={setFile} testPrefix="roll" />
        <label className={`mt-4 flex gap-3 items-start p-4 rounded-2xl border-2 cursor-pointer transition-colors ${copyright ? "border-[#10B981] bg-[#10B981]/10" : "border-[#ECE7E0]"}`} data-testid="roll-copyright">
          <input type="checkbox" className="sr-only" checked={copyright} onChange={(e) => setCopyright(e.target.checked)} data-testid="roll-copyright-checkbox" />
          <span className={`h-6 w-6 rounded-md border-2 flex-shrink-0 flex items-center justify-center ${copyright ? "bg-[#10B981] border-[#10B981] text-white" : "border-[color:var(--brand)]"}`} aria-hidden="true">{copyright && <Check size={16} strokeWidth={3} />}</span>
          <span className="text-sm font-semibold leading-snug">I confirm that I own this artwork or have permission from the copyright owner to reproduce and print it.</span>
        </label>
      </div>

      <div className="p-6 bg-[#FFF7F8] rounded-b-3xl lg:rounded-bl-none lg:rounded-r-3xl">
        <div className="eyebrow">Instant price</div>
        <PriceBlock result={result} q={q} />
        <button onClick={onAdd} className="btn-primary w-full mt-5" data-testid="custom-add-to-cart">Add to Cart</button>
        <p className="text-xs text-slate-500 mt-2 text-center">Prices in AUD. Minimum order ${ROLL_MIN_CHARGE} + GST.</p>
      </div>
    </div>
  );
}
